/**
 * Cookie consent state shared by the banner and the tag loader in public/index.html.
 *
 * Analytics/marketing tags (GA4, GTM) are never loaded until `acceptCookies()`
 * runs. `rejectCookies()` keeps them unloaded and clears anything a previous
 * (pre-consent) visit may have left behind. Only the consent cookie itself is
 * stored on rejection - it is strictly necessary to remember the refusal.
 */

export const CONSENT_COOKIE = 'ipma_cookie_consent';
export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_REJECTED = 'rejected';
export const CONSENT_EVENT = 'ipma:cookie-consent-open';

const CONSENT_DAYS = 180;

// Cookies dropped by GA4 / GTM / Google Ads that must go when consent is refused.
const TRACKING_COOKIE_PATTERNS = [
  /^_ga$/,
  /^_ga_/,
  /^_gid$/,
  /^_gat/,
  /^_gcl_/,
  /^_dc_gtm_/,
  /^__utm/,
];

// Consent cookie used by the previous accept-only banner.
const LEGACY_CONSENT_COOKIE = 'dcookie';

const canUseDom = () => typeof document !== 'undefined';

const readCookie = (name) => {
  if (!canUseDom()) return null;

  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.indexOf(prefix) === 0);

  return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
};

/**
 * Every domain a cookie could have been scoped to, so deletion actually lands.
 * `null` means "no domain attribute" (host-only cookie).
 */
const domainVariants = () => {
  const parts = window.location.hostname.split('.');
  const variants = [null];

  for (let i = 0; i < parts.length - 1; i += 1) {
    variants.push(`.${parts.slice(i).join('.')}`);
  }

  return variants;
};

const expireCookie = (name) => {
  if (!canUseDom()) return;

  domainVariants().forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
      domain ? `; domain=${domain}` : ''
    }`;
  });
};

const clearTrackingCookies = () => {
  if (!canUseDom()) return;

  document.cookie
    .split(';')
    .map((entry) => entry.split('=')[0].trim())
    .filter((name) => name && TRACKING_COOKIE_PATTERNS.some((pattern) => pattern.test(name)))
    .forEach(expireCookie);
};

const writeConsent = (value) => {
  if (!canUseDom()) return;

  const date = new Date();
  date.setTime(date.getTime() + CONSENT_DAYS * 24 * 60 * 60 * 1000);

  document.cookie = `${CONSENT_COOKIE}=${value}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;

  // The old banner only ever recorded an "accept"; that consent is not valid here.
  expireCookie(LEGACY_CONSENT_COOKIE);
};

export const getConsent = () => {
  const value = readCookie(CONSENT_COOKIE);

  return value === CONSENT_ACCEPTED || value === CONSENT_REJECTED ? value : null;
};

export const hasConsentDecision = () => getConsent() !== null;

export const acceptCookies = () => {
  writeConsent(CONSENT_ACCEPTED);

  if (typeof window !== 'undefined' && window.ipmaConsent) {
    window.ipmaConsent.grant();
  }
};

export const rejectCookies = () => {
  writeConsent(CONSENT_REJECTED);

  if (typeof window !== 'undefined' && window.ipmaConsent) {
    window.ipmaConsent.deny();
  }

  clearTrackingCookies();
};

/** Lets the user reopen the banner to withdraw or change consent. */
export const openCookieSettings = () => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
};
